import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useDemoMode } from './DemoMode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  {
    icon: '🏠',
    title_hi: 'आपका Demo Dashboard तैयार है',
    title_en: 'Your Demo Dashboard is Ready',
    body_hi: 'हमने आपके school में पहले से ही 15 classes, 125 students, 20 teachers और attendance/fee data populate कर दिया है ताकि आप real experience देख सकें।',
    body_en: 'We have pre-populated 15 classes, 125 students, 20 teachers and live attendance/fee data so you can experience the real product.',
  },
  {
    icon: '📊',
    title_hi: 'Live Attendance + Reports',
    title_en: 'Live Attendance + Reports',
    body_hi: 'पिछले 7 दिनों की attendance trends, gender distribution, monthly fee collection जैसे actionable graphs Dashboard पर मिलेंगे।',
    body_en: 'See last-7-days attendance trends, gender distribution, monthly fee collection — all actionable graphs ready on the dashboard.',
  },
  {
    icon: '🤖',
    title_hi: '31 AI Features (अभी Locked — Activate करें)',
    title_en: '31 AI Features (Locked — Activate to unlock)',
    body_hi: 'Aayush AI chatbot, Lesson Planner, Doubt Solver, Attendance OCR, Fee Reminder Bot — पूरा access तब मिलेगा जब आप payment करके Activate करें।',
    body_en: 'Aayush AI chatbot, Lesson Planner, Doubt Solver, Attendance OCR, Fee Bot — full access unlocks after you pay & activate.',
  },
  {
    icon: '🚀',
    title_hi: 'Activate करें — पूरा access',
    title_en: 'Activate — Full Access',
    body_hi: 'Software हमेशा FREE — सिर्फ़ one-time + छोटा सालाना maintenance, आपके कुल users (छात्र+शिक्षक+staff) के slab पर। Explore window खत्म होने से पहले payment करके अपना real data add करना शुरू करें (Guided Demo, payment के बाद full unlock)।',
    body_en: 'Software is always FREE — only a one-time + small yearly maintenance based on your total users (students+teachers+staff) slab. Pay before the explore window ends to start adding your real data (Guided Demo, full unlock after payment).',
  },
];

export default function WelcomeTour({ user, token }) {
  const { language } = useLanguage();
  const { state, openActivateModal } = useDemoMode();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Show only if demo + first login not done yet (prevent flash on every load)
  useEffect(() => {
    if (!user || !token) return;
    if (!state?.is_demo) return;
    if (user?.first_login_done) return;
    // If localStorage has the dismissal flag, don't show this session either.
    if (localStorage.getItem(`welcomeTour:dismissed:${user.id}`)) return;
    // Wait for the first-login language prompt to be dismissed first.
    const langPending = user?.role === 'school_admin' && !user?.lang_prompt_seen && !localStorage.getItem(`langPromptDone:${user.id}`);
    if (langPending) return;
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [user, token, state?.is_demo, user?.first_login_done]);

  // When the language prompt is dismissed, open the tour (after a short beat).
  useEffect(() => {
    const h = () => {
      if (!user || !state?.is_demo || user?.first_login_done) return;
      if (localStorage.getItem(`welcomeTour:dismissed:${user.id}`)) return;
      setTimeout(() => setOpen(true), 400);
    };
    window.addEventListener('lang-prompt:done', h);
    return () => window.removeEventListener('lang-prompt:done', h);
  }, [user, state?.is_demo]);

  // Replay support — anyone can fire window event 'welcome-tour:replay' to re-open the tour.
  useEffect(() => {
    const handler = () => { setStep(0); setOpen(true); };
    window.addEventListener('welcome-tour:replay', handler);
    return () => window.removeEventListener('welcome-tour:replay', handler);
  }, []);

  const t = (hi, en) => (language === 'hi' ? hi : en);

  const close = async (markDone = false) => {
    setOpen(false);
    try { localStorage.setItem(`welcomeTour:dismissed:${user.id}`, '1'); } catch { /* */ }
    if (markDone && token) {
      try {
        await fetch(`${API_URL}/api/demo-mode/mark-first-login-done`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* fail silently */ }
      // First login done → 14-day trial is now active. Open Diksha's warm guided demo.
      setTimeout(() => window.dispatchEvent(new Event('diksha-demo:start')), 400);
    }
  };

  if (!open) return null;
  const isLast = step === STEPS.length - 1;
  const cur = STEPS[step];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="welcome-tour">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Progress dots */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-5 pt-5 pb-3">
          <div className="flex items-center justify-between mb-3">
            <div className="text-white/90 text-xs font-semibold uppercase tracking-wide">
              {t(`चरण ${step + 1} / ${STEPS.length}`, `Step ${step + 1} / ${STEPS.length}`)}
            </div>
            <button onClick={() => close(true)} className="text-white/70 hover:text-white text-xl leading-none" aria-label="Skip" data-testid="welcome-tour-skip">×</button>
          </div>
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1 rounded-full flex-1 ${i <= step ? 'bg-white' : 'bg-white/30'}`} />
            ))}
          </div>
        </div>
        {/* Content */}
        <div className="p-6 text-center">
          <div className="text-5xl mb-3" aria-hidden>{cur.icon}</div>
          <h3 className="text-xl font-bold text-slate-900 mb-2" data-testid={`welcome-tour-step-${step}`}>
            {t(cur.title_hi, cur.title_en)}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed">{t(cur.body_hi, cur.body_en)}</p>
        </div>
        {/* Actions */}
        <div className="px-5 pb-5 flex gap-2">
          {step > 0 && (
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold"
              data-testid="welcome-tour-back"
            >
              {t('पीछे', 'Back')}
            </button>
          )}
          {!isLast ? (
            <button
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 hover:to-orange-600 text-white font-bold"
              data-testid="welcome-tour-next"
            >
              {t('अगला →', 'Next →')}
            </button>
          ) : (
            <>
              <button
                onClick={() => close(true)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold"
                data-testid="welcome-tour-explore"
              >
                {t('Explore करें', 'Explore')}
              </button>
              <button
                onClick={() => { close(true); openActivateModal(); }}
                className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:to-emerald-700 text-white font-bold"
                data-testid="welcome-tour-activate"
              >
                🚀 {t('Activate करें', 'Activate')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
